import { isElectron } from "@/services/storage/LocalFileSystemClient";
import { convex } from "@/lib/convexClient";
import { api } from "../../convex/_generated/api";
import { localDb, newId, now } from "@/lib/localDb";
import type {
  ColorVariable,
  SizeVariable,
  AngleVariable,
  FontFamilyVariable,
  IconVariable,
  ProjectVariables,
  CraftspaceVariables,
} from "@/store/variablesStore";

export type VarType = "color" | "size" | "angle" | "font_family" | "icon";

interface DbVariable {
  id: string;
  project_id: string | null;
  name: string;
  var_type: string;
  value: string;
  metadata: string | null;
  created_at: string;
  updated_at: string;
}

type ConvexVariable = {
  id: string;
  project_id?: string | null;
  name: string;
  var_type: string;
  value: string;
  metadata?: unknown;
};

export function rowsToVariables(rows: Array<DbVariable | ConvexVariable>): ProjectVariables {
  const colors: ColorVariable[] = [];
  const sizes: SizeVariable[] = [];
  const angles: AngleVariable[] = [];
  const fontFamilies: FontFamilyVariable[] = [];
  const icons: IconVariable[] = [];

  for (const row of rows) {
    switch (row.var_type) {
      case "color":
        colors.push({ id: row.id, name: row.name, value: row.value });
        break;
      case "size":
        sizes.push({ id: row.id, name: row.name, value: row.value });
        break;
      case "angle":
        angles.push({ id: row.id, name: row.name, value: Number(row.value) });
        break;
      case "font_family": {
        const meta =
          typeof (row as DbVariable).metadata === "string"
            ? (JSON.parse((row as DbVariable).metadata) as Record<string, string>)
            : ((row as ConvexVariable).metadata as Record<string, string> | null | undefined) || {};
        fontFamilies.push({
          id: row.id,
          name: row.name,
          value: row.value,
          src: meta.src,
          format: meta.format,
        });
        break;
      }
      case "icon":
        icons.push({ id: row.id, name: row.name, value: row.value });
        break;
    }
  }

  return { colors, sizes, angles, fontFamilies, icons };
}

export async function loadProjectVariables(
  craftspaceId: string,
  projectId: string,
): Promise<ProjectVariables> {
  if (!isElectron()) {
    const rows = await convex.query(api.variables.listByProject, { projectId });
    return rowsToVariables(rows as ConvexVariable[]);
  }
  const rows = await localDb(craftspaceId).query<DbVariable>(
    "SELECT * FROM variables WHERE project_id = ?",
    [projectId],
  );
  return rowsToVariables(rows);
}

export async function loadCraftspaceVariables(craftspaceId: string): Promise<CraftspaceVariables> {
  if (!isElectron()) {
    const rows = await convex.query(api.variables.listByCraftspace, { craftspaceId });
    return rowsToVariables(rows as ConvexVariable[]);
  }
  const rows = await localDb(craftspaceId).query<DbVariable>(
    "SELECT * FROM variables WHERE project_id IS NULL",
    [],
  );
  return rowsToVariables(rows);
}

export async function upsertVariable(
  craftspaceId: string,
  id: string,
  projectId: string | null,
  varType: VarType,
  name: string,
  value: string,
  metadata?: Record<string, unknown>,
): Promise<string> {
  if (!isElectron()) {
    const resolvedId = id.startsWith("temp-") ? undefined : id;
    const result = await convex.mutation(api.variables.upsert, {
      craftspaceId,
      projectId: projectId ?? undefined,
      id: resolvedId as any,
      varType,
      name,
      value,
      metadata,
    });
    return String(result);
  }
  await localDb(craftspaceId).run(
    `INSERT INTO variables (id, project_id, name, var_type, value, metadata, created_at, updated_at)
     VALUES (?, ?, ?, ?, ?, ?, ?, ?)
     ON CONFLICT(id) DO UPDATE SET
       name = excluded.name,
       value = excluded.value,
       metadata = excluded.metadata,
       updated_at = excluded.updated_at`,
    [
      id,
      projectId,
      name,
      varType,
      value,
      metadata ? JSON.stringify(metadata) : null,
      now(),
      now(),
    ],
  );
  return id;
}

export async function deleteVariable(craftspaceId: string, id: string): Promise<void> {
  if (!isElectron()) {
    await convex.mutation(api.variables.remove, { craftspaceId, id: id as any });
    return;
  }
  await localDb(craftspaceId).run("DELETE FROM variables WHERE id = ?", [id]);
}

export { newId };
